import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store/indexRedux";
import { fetchContractStatuses, SdpStatusEntry, StatusDirection } from "../api/contractStatus";

const DIRECTIONS: StatusDirection[] = ["injection", "offtake", "flex"];

export interface ContractStatusesState {
  /**
   * FT-1189 ④ — every SDP keyed by `installation_id` ALONE, directions merged.
   * Failed directions are simply absent: the original graceful-degradation
   * contract, relied on by PortfolioServices, which falls back to its legacy
   * status source. UNCHANGED — do not make this one throw or empty on error.
   */
  statuses: Map<string, SdpStatusEntry>;
  /** One combined loading state across all three direction calls. */
  // Existing public shape of this hook — FT-1189 consumers already destructure `loading`;
  // renaming it to `isLoading` is a breaking API change, not a lint fix.
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  loading: boolean;
  /**
   * FT-5702 Part 4 — the same entries keyed by `direction` and then by
   * `installation_id`. `statuses` merges the directions into one map, so an
   * installation_id present in more than one direction's SDP table would
   * silently overwrite. EOC reasons per LEG and must not risk that, so it
   * reads this instead.
   */
  byDirection: Map<StatusDirection, Map<string, SdpStatusEntry>>;
  /**
   * FT-5702 Part 4 — directions whose call REJECTED. A consumer that must not
   * show "you have nothing" for a failed request (end-of-contract) reads this;
   * consumers that degrade gracefully keep ignoring it. A direction listed
   * here contributes no entries to either map, which is exactly why a failure
   * is otherwise indistinguishable from an empty result.
   */
  failedDirections: StatusDirection[];
  /**
   * False until the first pass has settled (and again while a company change
   * is in flight). `loading` is false on the very first render — before the
   * effect has run — so a consumer gating only on `loading` renders one frame
   * of "no results" against an empty map.
   */
  // Existing public shape of this hook — FT-1189 consumers already destructure `loaded`;
  // renaming it to `hasLoaded` is a breaking API change, not a lint fix.
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  loaded: boolean;
  /**
   * Force a fresh pass. FT-5702 deliberately ships NO caching layer: the list
   * must be accurate at submit time, and staleness is the whole ticket. Used
   * after an EOC submit pass, alongside the portfolio refresh.
   */
  refetch: () => void;
}

/**
 * FT-1189 ④ — per-SDP contract statuses for the selected company.
 * All three directions are fired CONCURRENTLY (`Promise.allSettled`), never
 * in sequence, and share one combined loading state.
 */
export function useContractStatuses(companyId: number | null | undefined): ContractStatusesState {
  const accessToken = useSelector((state: RootState) => state.authv2.accessToken);
  const [statuses, setStatuses] = useState<Map<string, SdpStatusEntry>>(new Map());
  const [byDirection, setByDirection] = useState<Map<StatusDirection, Map<string, SdpStatusEntry>>>(
    new Map()
  );
  const [failedDirections, setFailedDirections] = useState<StatusDirection[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    if (!companyId || !accessToken) {
      setStatuses(new Map());
      setByDirection(new Map());
      setFailedDirections([]);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoaded(false);

    Promise.allSettled(DIRECTIONS.map((direction) => fetchContractStatuses(companyId, direction, accessToken)))
      .then((results) => {
        if (cancelled) return;
        const map = new Map<string, SdpStatusEntry>();
        const perDirection = new Map<StatusDirection, Map<string, SdpStatusEntry>>();
        const failed: StatusDirection[] = [];
        results.forEach((result, i) => {
          const direction = DIRECTIONS[i];
          if (result.status !== "fulfilled") {
            failed.push(direction);
            return;
          }
          const directionMap = new Map<string, SdpStatusEntry>();
          for (const sdp of result.value) {
            map.set(sdp.installation_id, sdp);
            directionMap.set(sdp.installation_id, sdp);
          }
          perDirection.set(direction, directionMap);
        });
        setStatuses(map);
        setByDirection(perDirection);
        setFailedDirections(failed);
        setLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, accessToken, reloadToken]);

  return { statuses, loading, byDirection, failedDirections, loaded, refetch };
}
